// V3 — profile factors. Derives a per-cell multiplier from `model.dimensions`
// using `profile.selections` + auto-derived `budget_bucket` (from totalBudget)
// and `lob_count` (from profile.lobs.length).

import type { ClientProfile, LevelId, Model } from '../types.js';
import { targetedFactorMultiplier } from './factors.js';

/** Find the budget bucket id matching profile.totalBudget. */
export function deriveBudgetBucketId(model: Model, totalBudget: number): string | undefined {
  const buckets = model.budgetBuckets || [];
  for (const b of buckets) {
    const min = Number(b.minUsd) || 0;
    const max = b.maxUsd == null ? Number.POSITIVE_INFINITY : Number(b.maxUsd);
    if (totalBudget >= min && totalBudget < max) return b.id;
  }
  // If totalBudget exceeds all top maxes, fall back to the last bucket.
  return buckets[buckets.length - 1]?.id;
}

/**
 * Map a literal count of LOBs to a bucket option value on the `lob_count`
 * dimension. Falls back to the closest bucket if the dimension uses a
 * different option set than the canonical 1 / 2 / 3_4 / 5_7 / 8_plus.
 */
export function deriveLobCountId(model: Model, lobCount: number): string | undefined {
  const dim = (model.dimensions || []).find((d) => d.id === 'lob_count');
  if (!dim || !dim.options?.length) return undefined;
  const n = Math.max(0, Math.floor(Number(lobCount) || 0));
  const canonical =
    n <= 1 ? '1' : n === 2 ? '2' : n <= 4 ? '3_4' : n <= 7 ? '5_7' : '8_plus';
  const exact = dim.options.find((o) => o.value === canonical);
  if (exact) return exact.value;
  // Fallback: pick the closest option by parsing leading integers in the value.
  let best: { id: string; dist: number } | undefined;
  for (const opt of dim.options) {
    const m = String(opt.value).match(/(\d+)/);
    const guess = m ? Number(m[1]) : NaN;
    if (!Number.isFinite(guess)) continue;
    const d = Math.abs(guess - n);
    if (!best || d < best.dist) best = { id: opt.value, dist: d };
  }
  return best?.id || dim.options[0]?.value;
}

/** Build an effective profile by auto-deriving budget_bucket and lob_count. */
export function withDerivedDimensions(model: Model, profile: ClientProfile): ClientProfile {
  const next: ClientProfile = {
    ...profile,
    selections: { ...(profile.selections || {}) },
  };
  if (!next.selections.budget_bucket) {
    const id = deriveBudgetBucketId(model, Number(profile.totalBudget) || 0);
    if (id) next.selections.budget_bucket = id;
  }
  // Auto-derive LOB count bucket from the LOB list length. Always recompute
  // (LOBs are pure UI state — there is no manual override to preserve).
  const lobN = Array.isArray(profile.lobs) ? profile.lobs.length : 0;
  const lobId = deriveLobCountId(model, lobN);
  if (lobId) next.selections.lob_count = lobId;
  return next;
}

/** Per-cell multiplier — re-exposed for engine. */
export function profileFactorMultiplier(
  model: Model,
  profile: ClientProfile,
  disciplineId: string,
  levelId: LevelId,
): number {
  return targetedFactorMultiplier(model, profile, disciplineId, levelId);
}
